// src/services/MultiLevelCommissionService.js - UPDATED: New 10-level commission structure
import {
  collection,
  addDoc,
  updateDoc,
  doc,
  query,
  where,
  getDocs,
  orderBy,
  limit,
} from "firebase/firestore";
import { db } from "../firebase/config";
import {
  REFERRAL_COMMISSION_MAX_LEVELS,
  REFERRAL_COMMISSION_RATES,
  REFERRAL_COMMISSION_TOTAL_PERCENT,
  REFERRAL_COMPANY_ROYALTY_PERCENT,
} from "../config/referralCommissionConfig";
import { calculateCartProfitMargin } from "../utils/cartProfitMargin";
import { isFlatMembershipPackage } from "../config/membershipReferralConfig";

class MultiLevelCommissionService {
  static COMMISSION_RATES = REFERRAL_COMMISSION_RATES;
  static MAX_LEVELS = REFERRAL_COMMISSION_MAX_LEVELS;
  static TOTAL_COMMISSION_PERCENTAGE = REFERRAL_COMMISSION_TOTAL_PERCENT;
  static COMPANY_ROYALTY_PERCENTAGE = REFERRAL_COMPANY_ROYALTY_PERCENT;
  static UNDISTRIBUTED_PROFIT_LABEL = "Undistributed Profit";

  // Get user's referral chain up to specified levels - OPTIMIZED
  static async getUserReferralChain(userId, maxLevels = this.MAX_LEVELS) {
    try {
      const referralChain = [];
      const referrerIds = [];
      let currentUserId = userId;
      let level = 1;

      // First pass: collect all referrer IDs in the chain
      while (level <= maxLevels && currentUserId) {
        const referralQuery = query(
          collection(db, "referrals"),
          where("referredUserId", "==", currentUserId)
        );
        const referralSnapshot = await getDocs(referralQuery);

        if (referralSnapshot.empty) break;

        const referrerId = referralSnapshot.docs[0].data().referrerId;
        referrerIds.push({ referrerId, level });
        currentUserId = referrerId;
        level++;
      }

      if (referrerIds.length === 0) return [];

      // Second pass: batch fetch all user details at once
      const userIds = referrerIds.map(r => r.referrerId);
      const userQuery = query(
        collection(db, "users"),
        where("uid", "in", userIds)
      );
      const userSnapshot = await getDocs(userQuery);
      
      // Create a map for quick lookup
      const userMap = new Map();
      userSnapshot.docs.forEach(doc => {
        const userData = doc.data();
        userMap.set(userData.uid, userData);
      });

      // Build the referral chain with user details
      for (const { referrerId, level } of referrerIds) {
        const referrerData = userMap.get(referrerId);
        if (referrerData) {
          referralChain.push({
            level,
            referrerId,
            referrerName: referrerData.name,
            referrerEmail: referrerData.email,
            hasPurchasedPackage: referrerData.hasPurchasedPackage || false,
            commissionRate: this.COMMISSION_RATES[level] || 0,
          });
        }
      }

      return referralChain;
    } catch (error) {
      console.error("Error getting referral chain:", error);
      return [];
    }
  }

  static isReferralSharingBlocked(orderData) {
    if (!orderData) return false;
    if (orderData.referralSharingBlocked === true) return true;

    const discounts = orderData.appliedDiscounts;
    if (!discounts) return false;

    // Cashback blocks all upline profit sharing, even if Points Earnings is also used
    return (
      discounts.usedCashback === true || (discounts.cashbackDiscount || 0) > 0
    );
  }

  static calculateOrderProfitMargin(orderData) {
    return calculateCartProfitMargin(orderData);
  }

  static getCashbackDiscountAmount(orderData) {
    return Math.max(0, orderData?.appliedDiscounts?.cashbackDiscount || 0);
  }

  // Process affiliate system commissions for an order with correct profit distribution
  static async processOrderCommissions(orderData) {
    try {
      console.log(
        "Processing affiliate system commissions for order:",
        orderData.cartOrderId
      );

      const results = {
        success: true,
        totalCommissionsCreated: 0,
        totalCommissionAmount: 0,
        commissionsCreated: [],
        errors: [],
        totalRoyalty: 0,
        totalProfitMargin: 0,
        undistributedProfit: 0,
        cashbackDeduction: 0,
        companyNetProfit: 0,
      };

      // Cashback discount: deduct from profit, send remainder to company (no referral sharing)
      if (this.isReferralSharingBlocked(orderData)) {
        const totalProfitMargin = this.calculateOrderProfitMargin(orderData);
        const cashbackDeduction = this.getCashbackDiscountAmount(orderData);
        const companyNetProfit = Math.max(0, totalProfitMargin - cashbackDeduction);

        results.totalProfitMargin = totalProfitMargin;
        results.cashbackDeduction = cashbackDeduction;
        results.companyNetProfit = companyNetProfit;
        results.totalRoyalty = companyNetProfit;

        console.log(
          `Cashback profit allocation for order ${orderData.cartOrderId}: profit ₹${totalProfitMargin.toFixed(2)}, cashback ₹${cashbackDeduction.toFixed(2)}, company ₹${companyNetProfit.toFixed(2)}`
        );

        if (totalProfitMargin > 0 || cashbackDeduction > 0) {
          const royaltyResult = await this.createCompanyRoyaltyRecord({
            orderId: orderData.cartOrderId,
            buyerId: orderData.buyerId,
            buyerName: orderData.buyerName,
            buyerEmail: orderData.buyerEmail,
            totalProfitMargin,
            totalCommissionAmount: 0,
            royaltyAmount: companyNetProfit,
            cashbackDeduction,
            companyNetProfit,
            undistributedProfit: 0,
            commissionPercentage: 0,
            royaltyPercentage: 100,
            commissionsCreated: 0,
            allocationType: "cashback_company_retention",
          });

          if (!royaltyResult.success) {
            results.errors.push(
              `Failed to create company profit record: ${royaltyResult.error}`
            );
          }
        }

        return {
          ...results,
          skippedReferralSharing: true,
          skipReason: "cashback_discount_applied",
        };
      }

      // Get referral chain for the buyer
      const referralChain = await this.getUserReferralChain(orderData.buyerId);

      if (referralChain.length === 0) {
        console.log("No referral chain found for buyer");
        return results;
      }

      console.log(`Found referral chain with ${referralChain.length} levels`);

      // Process commissions for items
      if (orderData.items && orderData.items.length > 0) {
        for (const item of orderData.items) {
          if (item.referralShare) {
            const itemResults = await this.processItemCommissions(
              item,
              orderData,
              referralChain
            );

            results.totalCommissionsCreated += itemResults.commissionsCreated;
            results.totalCommissionAmount += itemResults.totalCommissionAmount;
            results.totalRoyalty += itemResults.royaltyAmount;
            results.undistributedProfit += itemResults.undistributedProfit;
            results.totalProfitMargin +=
              (item.profitMargin || 0) * (item.quantity || 1);
            results.commissionsCreated.push(...itemResults.commissions);

            if (itemResults.errors.length > 0) {
              results.errors.push(...itemResults.errors);
            }
          } else {
            console.log(
              `Item ${item.name} does not have referral share enabled`
            );
          }
        }
      }

      // Process commissions for packages
      if (orderData.packages && orderData.packages.length > 0) {
        for (const pkg of orderData.packages) {
          if (pkg.referralShare) {
            const packageResults = await this.processPackageCommissions(
              pkg,
              orderData,
              referralChain
            );

            results.totalCommissionsCreated +=
              packageResults.commissionsCreated;
            results.totalCommissionAmount +=
              packageResults.totalCommissionAmount;
            results.totalRoyalty += packageResults.royaltyAmount;
            results.undistributedProfit += packageResults.undistributedProfit;

            // Calculate package profit margin
            const packagePrice = pkg.price || 0;
            const quantity = pkg.quantity || 1;
            const profitMargin = pkg.profitMargin || packagePrice * 0.1;
            results.totalProfitMargin += profitMargin * quantity;

            results.commissionsCreated.push(...packageResults.commissions);

            if (packageResults.errors.length > 0) {
              results.errors.push(...packageResults.errors);
            }
          } else {
            console.log(
              `Package ${pkg.name} does not have referral share enabled`
            );
          }
        }
      }

      // Create company royalty record with updated amounts
      if (results.totalProfitMargin > 0) {
        const royaltyResult = await this.createCompanyRoyaltyRecord({
          orderId: orderData.cartOrderId,
          buyerId: orderData.buyerId,
          buyerName: orderData.buyerName,
          buyerEmail: orderData.buyerEmail,
          totalProfitMargin: results.totalProfitMargin,
          totalCommissionAmount: results.totalCommissionAmount,
          royaltyAmount: results.totalRoyalty,
          undistributedProfit: results.undistributedProfit,
          commissionPercentage: this.TOTAL_COMMISSION_PERCENTAGE,
          royaltyPercentage: this.COMPANY_ROYALTY_PERCENTAGE,
          commissionsCreated: results.totalCommissionsCreated,
        });

        if (!royaltyResult.success) {
          results.errors.push(
            `Failed to create royalty record: ${royaltyResult.error}`
          );
        }
      }

      console.log(`Total points created: ${results.totalCommissionsCreated}`);
      console.log(
        `Total points amount: ₹${results.totalCommissionAmount.toFixed(2)}`
      );
      console.log(`Total company royalty: ₹${results.totalRoyalty.toFixed(2)}`);
      console.log(
        `Undistributed profit: ₹${results.undistributedProfit.toFixed(2)}`
      );
      console.log(
        `Total profit margin: ₹${results.totalProfitMargin.toFixed(2)}`
      );

      return results;
    } catch (error) {
      console.error("Error processing order commissions:", error);
      return {
        success: false,
        error: error.message,
        totalCommissionsCreated: 0,
        totalCommissionAmount: 0,
        commissionsCreated: [],
        errors: [error.message],
        totalRoyalty: 0,
        totalProfitMargin: 0,
        undistributedProfit: 0,
      };
    }
  }

  // Process commissions for a single item with correct profit calculation
  static async processItemCommissions(item, orderData, referralChain) {
    try {
      const results = {
        commissionsCreated: 0,
        totalCommissionAmount: 0,
        royaltyAmount: 0,
        undistributedProfit: 0,
        commissions: [],
        errors: [],
      };

      const profitMargin = item.profitMargin || 0;
      const quantity = item.quantity || 1;
      const totalProfitMargin = profitMargin * quantity;

      if (totalProfitMargin <= 0) {
        console.log(`No profit margin for item ${item.name}`);
        return results;
      }

      console.log(
        `Processing commissions for item: ${item.name}, Total Profit: ₹${totalProfitMargin}`
      );

      let totalDistributedPercentage = 0;

      // Create commissions for each level in the referral chain
      for (const referrer of referralChain) {
        // Only create commission if referrer has purchased a package
        if (!referrer.hasPurchasedPackage) {
          console.log(
            `Referrer ${referrer.referrerEmail} at level ${referrer.level} has not purchased a package, skipping commission`
          );
          continue;
        }

        const commissionPercentage = referrer.commissionRate;
        const commissionAmount =
          (totalProfitMargin * commissionPercentage) / 100;

        if (commissionAmount > 0) {
          const commissionResult = await this.createCommissionRecord({
            referrerId: referrer.referrerId,
            referrerName: referrer.referrerName,
            referrerEmail: referrer.referrerEmail,
            buyerId: orderData.buyerId,
            buyerName: orderData.buyerName,
            buyerEmail: orderData.buyerEmail,
            orderId: orderData.cartOrderId,
            orderType: "item",
            itemId: item.id,
            itemName: item.name,
            itemQuantity: quantity,
            profitMargin: totalProfitMargin,
            commissionLevel: referrer.level,
            commissionPercentage: commissionPercentage,
            commissionAmount: commissionAmount,
            orderAmount: item.sellingPrice * quantity,
          });

          if (commissionResult.success) {
            results.commissionsCreated++;
            results.totalCommissionAmount += commissionAmount;
            results.commissions.push(commissionResult);
            totalDistributedPercentage += commissionPercentage;

            console.log(
              `Created points for Level ${referrer.level}: ${
                referrer.referrerEmail
              } - ₹${commissionAmount.toFixed(2)}`
            );
          } else {
            results.errors.push(
              `Failed to create points for ${referrer.referrerEmail}: ${commissionResult.error}`
            );
          }
        }
      }

      results.royaltyAmount = 0;
      const undistributedPercentage =
        this.TOTAL_COMMISSION_PERCENTAGE - totalDistributedPercentage;
      results.undistributedProfit =
        (totalProfitMargin * Math.max(0, undistributedPercentage)) / 100;

      console.log(
        `Item ${item.name}: Distributed ${totalDistributedPercentage}% of ${this.TOTAL_COMMISSION_PERCENTAGE}%, Undistributed: ₹${results.undistributedProfit.toFixed(2)}`
      );

      return results;
    } catch (error) {
      console.error("Error processing item commissions:", error);
      return {
        commissionsCreated: 0,
        totalCommissionAmount: 0,
        royaltyAmount: 0,
        undistributedProfit: 0,
        commissions: [],
        errors: [error.message],
      };
    }
  }

  // Process commissions for a single package with correct profit calculation
  static async processPackageCommissions(pkg, orderData, referralChain) {
    try {
      const results = {
        commissionsCreated: 0,
        totalCommissionAmount: 0,
        royaltyAmount: 0,
        undistributedProfit: 0,
        commissions: [],
        errors: [],
      };

      // For packages, we need to calculate profit margin
      const packagePrice = pkg.price || 0;
      const quantity = pkg.quantity || 1;
      const totalPackageAmount = packagePrice * quantity;

      // For packages, we might need to get profit margin from package data
      const profitMargin = pkg.profitMargin || packagePrice * 0.1; // Default 10% if not specified
      const totalProfitMargin = profitMargin * quantity;

      if (totalProfitMargin <= 0) {
        console.log(`No profit margin for package ${pkg.name}`);
        return results;
      }

      console.log(
        `Processing commissions for package: ${pkg.name}, Total Profit: ₹${totalProfitMargin}`
      );

      let totalDistributedPercentage = 0;

      // Create commissions for each level in the referral chain
      for (const referrer of referralChain) {
        // Only create commission if referrer has purchased a package
        if (!referrer.hasPurchasedPackage) {
          console.log(
            `Referrer ${referrer.referrerEmail} at level ${referrer.level} has not purchased a package, skipping commission`
          );
          continue;
        }

        const commissionPercentage = referrer.commissionRate;
        const commissionAmount =
          (totalProfitMargin * commissionPercentage) / 100;

        if (commissionAmount > 0) {
          const commissionResult = await this.createCommissionRecord({
            referrerId: referrer.referrerId,
            referrerName: referrer.referrerName,
            referrerEmail: referrer.referrerEmail,
            buyerId: orderData.buyerId,
            buyerName: orderData.buyerName,
            buyerEmail: orderData.buyerEmail,
            orderId: orderData.cartOrderId,
            orderType: "package",
            packageId: pkg.id,
            packageName: pkg.name,
            packageQuantity: quantity,
            profitMargin: totalProfitMargin,
            commissionLevel: referrer.level,
            commissionPercentage: commissionPercentage,
            commissionAmount: commissionAmount,
            orderAmount: totalPackageAmount,
            packagePrice: packagePrice || orderData.totalAmount,
          });

          if (commissionResult.success) {
            results.commissionsCreated++;
            results.totalCommissionAmount += commissionAmount;
            results.commissions.push(commissionResult);
            totalDistributedPercentage += commissionPercentage;

            console.log(
              `Created points for Level ${referrer.level}: ${
                referrer.referrerEmail
              } - ₹${commissionAmount.toFixed(2)}`
            );
          } else {
            results.errors.push(
              `Failed to create points for ${referrer.referrerEmail}: ${commissionResult.error}`
            );
          }
        }
      }

      results.royaltyAmount = 0;
      const undistributedPercentage =
        this.TOTAL_COMMISSION_PERCENTAGE - totalDistributedPercentage;
      results.undistributedProfit =
        (totalProfitMargin * Math.max(0, undistributedPercentage)) / 100;

      console.log(
        `Package ${pkg.name}: Distributed ${totalDistributedPercentage}% of ${this.TOTAL_COMMISSION_PERCENTAGE}%, Undistributed: ₹${results.undistributedProfit.toFixed(2)}`
      );

      return results;
    } catch (error) {
      console.error("Error processing package commissions:", error);
      return {
        commissionsCreated: 0,
        totalCommissionAmount: 0,
        royaltyAmount: 0,
        undistributedProfit: 0,
        commissions: [],
        errors: [error.message],
      };
    }
  }

  // Create a commission record in the database
  static async createCommissionRecord(commissionData) {
    try {
      // Generate commission ID
      const commissionId = `MLCOMM-${Date.now()}-${Math.random()
        .toString(36)
        .substr(2, 9)}`;

      const commissionRecord = {
        commissionId,
        type: `${commissionData.orderType}_multilevel_referral`,
        orderId: commissionData.orderId,
        orderType: commissionData.orderType,

        // Referrer details
        referrerId: commissionData.referrerId,
        referrerName: commissionData.referrerName,
        referrerEmail: commissionData.referrerEmail,

        // Buyer details
        buyerId: commissionData.buyerId,
        buyerName: commissionData.buyerName,
        buyerEmail: commissionData.buyerEmail,

        // Commission details
        commissionLevel: commissionData.commissionLevel,
        commissionPercentage: commissionData.commissionPercentage,
        commissionAmount:
          Math.round(commissionData.commissionAmount * 100) / 100, // Round to 2 decimal places

        // Order details
        orderAmount: commissionData.orderAmount,
        profitMargin: commissionData.profitMargin,

        // Item/Package specific details
        ...(commissionData.orderType === "item" && {
          itemId: commissionData.itemId,
          itemName: commissionData.itemName,
          itemQuantity: commissionData.itemQuantity,
        }),
        ...(commissionData.orderType === "package" && {
          packageId: commissionData.packageId,
          packageName: commissionData.packageName,
          packageQuantity: commissionData.packageQuantity,
        }),

        // Status and timestamps
        status: "confirmed", // Affiliate system commissions are auto-confirmed when order is confirmed
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        confirmedAt: new Date().toISOString(),

        // Metadata
        eligibilityChecked: true,
        referrerPackagePurchaseStatus: true,
        isMultiLevelCommission: true,
        notes: `Level ${commissionData.commissionLevel} referral points (${commissionData.commissionPercentage}% of profit margin)`,
      };

      const payout = await this.creditReferrerPayout(
        commissionData.referrerId,
        commissionRecord.commissionAmount,
        {
          orderType: commissionData.orderType,
          packagePrice: commissionData.packagePrice ?? commissionData.orderAmount,
          orderAmount: commissionData.orderAmount,
        }
      );
      if (payout.wallet === "royalty") {
        commissionRecord.payoutWallet = "royalty";
        commissionRecord.notes = `Level ${commissionData.commissionLevel} membership margin referral (Cash Wallet; ${commissionData.commissionPercentage}% of profit margin)`;
      } else {
        commissionRecord.payoutWallet = "points";
      }

      // Add commission to database
      const commissionRef = await addDoc(
        collection(db, "commissions"),
        commissionRecord
      );

      console.log(`Commission created with ID: ${commissionRef.id}`);

      // Update referrer's commission stats
      await this.updateUserCommissionStats(
        commissionData.referrerId,
        commissionRecord.commissionAmount,
        "add"
      );

      return {
        success: true,
        commissionId,
        commissionAmount: commissionRecord.commissionAmount,
        docId: commissionRef.id,
        level: commissionData.commissionLevel,
        referrerEmail: commissionData.referrerEmail,
      };
    } catch (error) {
      console.error("Error creating commission record:", error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  // Create a company royalty record with undistributed profit tracking
  static async createCompanyRoyaltyRecord(royaltyData) {
    try {
      const royaltyId = `ROYALTY-${Date.now()}-${Math.random()
        .toString(36)
        .substr(2, 9)}`;

      const royaltyRecord = {
        royaltyId,
        type: "company_royalty",
        orderId: royaltyData.orderId,

        // Buyer details
        buyerId: royaltyData.buyerId,
        buyerName: royaltyData.buyerName,
        buyerEmail: royaltyData.buyerEmail,

        // Financial breakdown
        totalProfitMargin:
          Math.round(royaltyData.totalProfitMargin * 100) / 100,
        totalCommissionAmount:
          Math.round(royaltyData.totalCommissionAmount * 100) / 100,
        royaltyAmount: Math.round(royaltyData.royaltyAmount * 100) / 100,
        undistributedProfit:
          Math.round((royaltyData.undistributedProfit || 0) * 100) / 100,
        cashbackDeduction:
          Math.round((royaltyData.cashbackDeduction || 0) * 100) / 100,
        companyNetProfit:
          Math.round(
            (royaltyData.companyNetProfit ?? royaltyData.royaltyAmount ?? 0) *
              100
          ) / 100,
        allocationType: royaltyData.allocationType || "standard",

        // Percentages
        commissionPercentage: royaltyData.commissionPercentage,
        royaltyPercentage: royaltyData.royaltyPercentage,

        // Commission details
        commissionsCreated: royaltyData.commissionsCreated,

        // Timestamps
        createdAt: new Date().toISOString(),
        status: "confirmed",

        // Metadata
        notes:
          royaltyData.allocationType === "cashback_company_retention"
            ? `Cashback order: profit ₹${royaltyData.totalProfitMargin.toFixed(
                2
              )} - cashback ₹${(royaltyData.cashbackDeduction || 0).toFixed(
                2
              )} = company ₹${(royaltyData.companyNetProfit ?? 0).toFixed(2)}`
            : `Company royalty: ${
                royaltyData.royaltyPercentage
              }% of profit margin from order ${royaltyData.orderId}${
                royaltyData.undistributedProfit > 0
                  ? ` | Undistributed profit: ₹${royaltyData.undistributedProfit.toFixed(
                      2
                    )}`
                  : ""
              }`,
      };

      const royaltyRef = await addDoc(
        collection(db, "companyRoyalties"),
        royaltyRecord
      );

      console.log(
        `✅ Company royalty recorded: ₹${royaltyData.royaltyAmount.toFixed(
          2
        )} (${
          royaltyData.royaltyPercentage
        }% of ₹${royaltyData.totalProfitMargin.toFixed(2)})`
      );

      if (royaltyData.undistributedProfit > 0) {
        console.log(
          `📊 Undistributed profit: ₹${royaltyData.undistributedProfit.toFixed(
            2
          )} (due to fewer referral levels)`
        );
      }

      return {
        success: true,
        royaltyId,
        royaltyAmount: royaltyRecord.royaltyAmount,
        undistributedProfit: royaltyRecord.undistributedProfit,
        docId: royaltyRef.id,
      };
    } catch (error) {
      console.error("Error creating company royalty record:", error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  // Update user's commission statistics
  static async creditReferrerPayout(
    referrerId,
    commissionAmount,
    { orderType, packagePrice, orderAmount } = {}
  ) {
    const flatMembershipPackage =
      orderType === "package" &&
      isFlatMembershipPackage(
        null,
        packagePrice ?? orderAmount
      );

    const userQuery = query(
      collection(db, "users"),
      where("uid", "==", referrerId)
    );
    const userSnapshot = await getDocs(userQuery);
    if (userSnapshot.empty) return { wallet: null };

    const userDocRef = doc(db, "users", userSnapshot.docs[0].id);
    const currentData = userSnapshot.docs[0].data();
    const amt = Number(commissionAmount) || 0;
    const now = new Date().toISOString();

    if (flatMembershipPackage) {
      await updateDoc(userDocRef, {
        royaltyBalance: (Number(currentData.royaltyBalance) || 0) + amt,
        royaltyEarned: (Number(currentData.royaltyEarned) || 0) + amt,
        updatedAt: now,
      });
      return { wallet: "royalty" };
    }

    await updateDoc(userDocRef, {
      commissionBalance: (Number(currentData.commissionBalance) || 0) + amt,
      commissionEarned: (Number(currentData.commissionEarned) || 0) + amt,
      updatedAt: now,
    });
    return { wallet: "points" };
  }

  static async updateUserCommissionStats(
    userId,
    commissionAmount,
    operation = "add"
  ) {
    try {
      const userQuery = query(
        collection(db, "users"),
        where("uid", "==", userId)
      );
      const userSnapshot = await getDocs(userQuery);

      if (!userSnapshot.empty) {
        const userDocRef = doc(db, "users", userSnapshot.docs[0].id);
        const currentData = userSnapshot.docs[0].data();

        const currentConfirmed = currentData.confirmedCommissions || 0;
        const currentTotal = currentData.totalCommissions || 0;

        let newConfirmed, newTotal;

        if (operation === "add") {
          newConfirmed = currentConfirmed + commissionAmount;
          newTotal = currentTotal + commissionAmount;
        } else if (operation === "subtract") {
          newConfirmed = Math.max(0, currentConfirmed - commissionAmount);
          newTotal = Math.max(0, currentTotal - commissionAmount);
        }

        await updateDoc(userDocRef, {
          confirmedCommissions: newConfirmed,
          totalCommissions: newTotal,
          updatedAt: new Date().toISOString(),
        });

        console.log(
          `User commission stats updated for ${userId}: ${currentConfirmed} -> ${newConfirmed}`
        );
      }
    } catch (error) {
      console.error("Error updating user commission stats:", error);
    }
  }

  // Get commission summary for an order
  static async getOrderCommissionSummary(orderId) {
    try {
      const commissionsQuery = query(
        collection(db, "commissions"),
        where("orderId", "==", orderId),
        where("isMultiLevelCommission", "==", true),
        orderBy("commissionLevel", "asc")
      );
      const commissionsSnapshot = await getDocs(commissionsQuery);

      const commissions = commissionsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Get company royalty for this order
      const royaltyQuery = query(
        collection(db, "companyRoyalties"),
        where("orderId", "==", orderId)
      );
      const royaltySnapshot = await getDocs(royaltyQuery);
      const royaltyData = royaltySnapshot.empty
        ? null
        : royaltySnapshot.docs[0].data();

      const totalCommissionAmount = commissions.reduce(
        (sum, commission) => sum + (commission.commissionAmount || 0),
        0
      );

      const levelBreakdown = commissions.reduce((breakdown, commission) => {
        const level = commission.commissionLevel;
        if (!breakdown[level]) {
          breakdown[level] = {
            level,
            count: 0,
            totalAmount: 0,
            referrers: [],
          };
        }
        breakdown[level].count++;
        breakdown[level].totalAmount += commission.commissionAmount;
        breakdown[level].referrers.push({
          referrerEmail: commission.referrerEmail,
          amount: commission.commissionAmount,
        });
        return breakdown;
      }, {});

      return {
        success: true,
        orderId,
        totalCommissions: commissions.length,
        totalCommissionAmount,
        commissions,
        levelBreakdown,
        companyRoyalty: royaltyData,
      };
    } catch (error) {
      console.error("Error getting order commission summary:", error);
      return {
        success: false,
        error: error.message,
        totalCommissions: 0,
        totalCommissionAmount: 0,
        commissions: [],
        levelBreakdown: {},
        companyRoyalty: null,
      };
    }
  }

  // Get user's affiliate system commission stats
  static async getUserMultiLevelStats(userId) {
    try {
      const commissionsQuery = query(
        collection(db, "commissions"),
        where("referrerId", "==", userId),
        where("isMultiLevelCommission", "==", true),
        orderBy("createdAt", "desc")
      );
      const commissionsSnapshot = await getDocs(commissionsQuery);

      const commissions = commissionsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      const totalEarnings = commissions.reduce(
        (sum, commission) => sum + (commission.commissionAmount || 0),
        0
      );

      const levelBreakdown = commissions.reduce((breakdown, commission) => {
        const level = commission.commissionLevel;
        if (!breakdown[level]) {
          breakdown[level] = {
            level,
            count: 0,
            totalAmount: 0,
            commissionRate: this.COMMISSION_RATES[level] || 0,
          };
        }
        breakdown[level].count++;
        breakdown[level].totalAmount += commission.commissionAmount;
        return breakdown;
      }, {});

      return {
        success: true,
        userId,
        totalCommissions: commissions.length,
        totalEarnings,
        commissions,
        levelBreakdown,
        commissionRates: this.COMMISSION_RATES,
      };
    } catch (error) {
      console.error("Error getting user affiliate system stats:", error);
      return {
        success: false,
        error: error.message,
        totalCommissions: 0,
        totalEarnings: 0,
        commissions: [],
        levelBreakdown: {},
      };
    }
  }

  // Get company royalty statistics with undistributed profit tracking
  static async getCompanyRoyaltyStats() {
    try {
      const royaltiesQuery = query(
        collection(db, "companyRoyalties"),
        orderBy("createdAt", "desc")
      );
      const royaltiesSnapshot = await getDocs(royaltiesQuery);

      const royalties = royaltiesSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      const totalRoyaltyAmount = royalties.reduce(
        (sum, royalty) => sum + (royalty.royaltyAmount || 0),
        0
      );

      const totalUndistributedProfit = royalties.reduce(
        (sum, royalty) => sum + (royalty.undistributedProfit || 0),
        0
      );

      const totalProfitMargin = royalties.reduce(
        (sum, royalty) => sum + (royalty.totalProfitMargin || 0),
        0
      );

      const totalCommissionsPaid = royalties.reduce(
        (sum, royalty) => sum + (royalty.totalCommissionAmount || 0),
        0
      );

      return {
        success: true,
        totalRoyalties: royalties.length,
        totalRoyaltyAmount,
        totalUndistributedProfit,
        totalProfitMargin,
        totalCommissionsPaid,
        royalties,
        averageRoyaltyPercentage:
          royalties.length > 0
            ? royalties.reduce(
                (sum, r) => sum + (r.royaltyPercentage || 0),
                0
              ) / royalties.length
            : 0,
      };
    } catch (error) {
      console.error("Error getting company royalty stats:", error);
      return {
        success: false,
        error: error.message,
        totalRoyalties: 0,
        totalRoyaltyAmount: 0,
        totalUndistributedProfit: 0,
        totalProfitMargin: 0,
        totalCommissionsPaid: 0,
        royalties: [],
      };
    }
  }

  // Get profit distribution breakdown for analytics
  static async getProfitDistributionBreakdown() {
    try {
      const royalties = await this.getCompanyRoyaltyStats();

      if (!royalties.success) {
        return royalties;
      }

      const breakdown = {
        totalProfitMargin: royalties.totalProfitMargin,
        distributedCommissions: royalties.totalCommissionsPaid,
        companyRoyalty: royalties.totalRoyaltyAmount,
        undistributedProfit: royalties.totalUndistributedProfit,

        // Percentages
        distributedCommissionPercentage:
          royalties.totalProfitMargin > 0
            ? (royalties.totalCommissionsPaid / royalties.totalProfitMargin) *
              100
            : 0,
        companyRoyaltyPercentage: this.COMPANY_ROYALTY_PERCENTAGE,
        undistributedProfitPercentage:
          royalties.totalProfitMargin > 0
            ? (royalties.totalUndistributedProfit /
                royalties.totalProfitMargin) *
              100
            : 0,
      };

      console.log("=== UPDATED PROFIT DISTRIBUTION BREAKDOWN (10 LEVELS) ===");
      console.log(
        `Total Profit Margin: ₹${breakdown.totalProfitMargin.toFixed(2)}`
      );
      console.log(
        `Distributed Commissions: ₹${breakdown.distributedCommissions.toFixed(
          2
        )} (${breakdown.distributedCommissionPercentage.toFixed(1)}%)`
      );
      console.log(
        `Company Royalty: ₹${breakdown.companyRoyalty.toFixed(2)} (${
          breakdown.companyRoyaltyPercentage
        }%)`
      );
      console.log(
        `Undistributed Profit: ₹${breakdown.undistributedProfit.toFixed(
          2
        )} (${breakdown.undistributedProfitPercentage.toFixed(1)}%)`
      );

      return {
        success: true,
        breakdown,
      };
    } catch (error) {
      console.error("Error getting profit distribution breakdown:", error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  // NEW: Helper method to display commission rates structure
  static getCommissionRatesBreakdown() {
    const rates = Object.entries(this.COMMISSION_RATES).map(
      ([level, rate]) => ({
        level: parseInt(level),
        rate: rate,
        description: `Level ${level}: ${rate}%`,
      })
    );

    const totalCommissionRate = Object.values(this.COMMISSION_RATES).reduce(
      (sum, rate) => sum + rate,
      0
    );

    console.log("=== UPDATED COMMISSION STRUCTURE ===");
    rates.forEach(({ level, rate, description }) => {
      console.log(description);
    });
    console.log(`Total Commission Rate: ${totalCommissionRate}%`);
    console.log(`Company Royalty: ${this.COMPANY_ROYALTY_PERCENTAGE}%`);
    console.log(
      `Total: ${totalCommissionRate + this.COMPANY_ROYALTY_PERCENTAGE}%`
    );

    return {
      rates,
      totalCommissionRate,
      companyRoyaltyRate: this.COMPANY_ROYALTY_PERCENTAGE,
      maxLevels: this.MAX_LEVELS,
    };
  }
}

export default MultiLevelCommissionService;
